import { useState } from "react";
import { MessageCircle, Phone, Copy, Check } from "lucide-react";
import { ASSETS, CONTACT } from "../constants";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, SectionHead } from "./Reveal";
import SocialIcons from "./SocialIcons";

const PhoneCard = ({ flag, label, note, display, tel, testId }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(display);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      /* clipboard unavailable */
    }
  };

  return (
    <div data-testid={`contact-card-${testId}`} className="flex flex-col rounded-3xl border border-white/[0.07] bg-panel/80 p-7 backdrop-blur-md transition-colors hover:border-gold/30">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
        {flag} {label}
      </p>
      <a href={tel} data-testid={`contact-tel-${testId}`} dir="ltr" className="mt-3 font-display text-2xl font-bold text-white transition-colors hover:text-gold sm:text-3xl">
        {display}
      </a>
      <p className="mt-2 text-xs text-slate-500">{note}</p>
      <div className="mt-6 flex gap-2.5">
        <a
          href={tel}
          data-testid={`contact-call-${testId}`}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-white/[0.06] py-3 text-[11px] font-bold uppercase tracking-widest text-white transition-all hover:bg-gold hover:text-[#08080A]"
        >
          <Phone className="h-4 w-4" aria-hidden="true" />
          {t("contact.callUs")}
        </a>
        <button
          type="button"
          data-testid={`contact-copy-${testId}`}
          onClick={copy}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-300 transition-colors hover:border-gold/50 hover:text-gold"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-400" aria-hidden="true" /> : <Copy className="h-4 w-4" aria-hidden="true" />}
          {copied ? t("contact.copied") : t("contact.copy")}
        </button>
      </div>
    </div>
  );
};

const ContactSection = () => {
  const { t } = useLanguage();

  return (
    <section id="contact" data-testid="contact-section" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={ASSETS.dubaiDusk} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A] via-[#08080A]/75 to-[#08080A]" />
      </div>
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <SectionHead eyebrow={t("contact.eyebrow")} title={t("contact.title")} sub={t("contact.text")} />
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          <Reveal>
            <a
              data-testid="contact-whatsapp-main"
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col justify-between rounded-3xl border border-[#25D366]/30 bg-[#25D366]/[0.07] p-7 backdrop-blur-md transition-all hover:border-[#25D366]/60 hover:shadow-[0_24px_60px_-24px_rgba(37,211,102,0.35)]"
            >
              <MessageCircle className="h-9 w-9 text-[#25D366]" aria-hidden="true" />
              <div className="mt-8">
                <p className="font-display text-3xl font-bold text-white">{t("contact.whatsapp")}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-400" dir="ltr">
                  {CONTACT.deDisplay}
                </p>
                <span
                  data-testid="contact-whatsapp-alt"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(CONTACT.whatsappAlt, "_blank", "noopener,noreferrer");
                  }}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && window.open(CONTACT.whatsappAlt, "_blank", "noopener,noreferrer")}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-[#25D366] transition-all hover:bg-[#25D366] hover:text-[#08080A]"
                >
                  <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("contact.whatsappAlt")}
                </span>
              </div>
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <PhoneCard flag="🇦🇪" label={t("contact.uae")} note={t("contact.uaeNote")} display={CONTACT.uaeDisplay} tel={CONTACT.uaeTel} testId="uae" />
          </Reveal>
          <Reveal delay={0.2}>
            <PhoneCard flag="🇩🇪" label={t("contact.germany")} note={t("contact.germanyNote")} display={CONTACT.deDisplay} tel={CONTACT.deTel} testId="germany" />
          </Reveal>
        </div>
        <Reveal delay={0.25} className="mt-10">
          <div className="flex flex-col items-start gap-4 rounded-3xl border border-white/[0.07] bg-panel/60 p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">{t("footer.socialTitle")}</p>
            <SocialIcons size="lg" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
